import { skipToken, type SuspenseQueryHookFetchPolicy, useSuspenseQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { useMemo } from "react";

const FACILITY_MONTHLY_GENERATION = gql(`
  query FacilityMonthlyGeneration($facilityId: ID!, $year: Int!) {
    facility(id: $facilityId) {
      id
      monthlyGeneration(year: $year) {
        month
        hazardousKg
        acuteKg
        fillEventCount
        incompleteFillCount
        status
      }
    }
  }
`);

// Container edits happen on other routes, so revalidate on mount.
const REVALIDATE_ON_MOUNT: SuspenseQueryHookFetchPolicy = "cache-and-network";

/**
 * Generation-driven monthly generation (kg) + status for a facility-year, for
 * the Generator Status card's generation column. Skips entirely unless
 * `enabled` (the ContainerTracking flag) is on, so it stays dark for tenants
 * that don't consume generation.
 */
export const useMonthlyGeneration = (
  facilityId: string | undefined,
  year: number | null,
  enabled: boolean
) => {
  const options = useMemo(() => {
    if (!enabled || !facilityId || year === null) {
      return skipToken;
    }
    return { variables: { facilityId, year }, fetchPolicy: REVALIDATE_ON_MOUNT };
  }, [enabled, facilityId, year]);
  return useSuspenseQuery(FACILITY_MONTHLY_GENERATION, options);
};
